import { useState, useEffect } from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import CustomTextDialog from './custom-text-dialog';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export default function AttributesFields({ part, setPart, changeHandler, error, units, unitsChangeHandler }) {
  const [visibleAttrs, setVisibleAttrs] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogNameValue, setDialogNameValue] = useState("");

  const attr0Name = part?.partCategory?.attributes?.["0"]?.attribute;

  useEffect(() => {
    // Set initial visible attributes when part category is set/changed
    if (part.partCategory && part.partCategory.attributes) {
      setVisibleAttrs(Object.keys(part.partCategory.attributes));
    } else {
      setVisibleAttrs([]);
    }
  }, [part.partCategory])

  useEffect(() => {
    // When the first attribute changes, update visibleAttrs based on conditional logic
    const attrs = part.partCategory.attributes;
    if (!attrs || !attrs["0"] || !attrs["0"].enableCondAttrs) {
      setVisibleAttrs(Object.keys(attrs || {}));
      return;
    }
    const selected = part.attributes?.[attrs["0"].attribute] || "";
    if (!selected) {
      setVisibleAttrs(["0"]);
      return;
    }
    const indexes = (attrs["0"][selected] || "")
      .split(";")
      .map(idx => idx.trim())
      .filter(idx => idx && idx !== "0");
    setVisibleAttrs(["0", ...indexes]);
  }, [attr0Name, part.attributes?.[attr0Name]]);

  // Handles multi select change
  const multiSelectChangeHandler = (event, key) => {
    const { value } = event.target;
    setPart(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [key]: typeof value === 'string' ? value.split(';') : value,
      }
    }));
  };

  // Handles attribute change, with special logic for the first attribute
  const handleAttributeChange = (event) => {
    const { name, value } = event.target;
    // name is in the form "attributes.{key}"
    const match = name.match(/^attributes\.(.+)$/);

    if (!match) {
      changeHandler(event);
      return;
    }

    const key = match[1];
    if (value === "\u2063{Custom Value Mode}\u2063") {
      setDialogNameValue(name);
      setOpenDialog(true);
    } // If the first attribute is changed, clear all other attributes except the first
    else if (part.partCategory.attributes && part.partCategory.attributes["0"] && key === part.partCategory.attributes["0"].attribute) {
      setPart(prev => ({
        ...prev,
        attributes: {
          [key]: value
        }
      }));
    } else {
      changeHandler(event);
    }

  };
  const handleCloseDialog = (customValue) => {
    setOpenDialog(false);

    if (customValue) {
      handleAttributeChange({ target: { name: dialogNameValue, value: customValue } });
    }

    setDialogNameValue("");
  }
  // Render attribute fields
  const renderAttributeFields = () => {
    const attrs = part?.partCategory?.attributes ? { ...part.partCategory.attributes } : {};

    return Object.entries(attrs).map(([num, attrObj]) => {
      const key = attrObj.attribute || `Attribute ${num}`;
      const options = attrObj.options || "";
      const unitsList = attrObj.units || "";
      const fieldType = attrObj.fieldType || "text";
      const enableMultiVals = !!attrObj.enableMultiVals;
      const multiSelectValues = Array.isArray(part.attributes?.[key]) ? part.attributes[key] : [];
      const validOptions = options.split(";");
      const filteredMultiSelectValues = multiSelectValues.filter(val => validOptions.includes(val));
      const hasHintText = attrObj.hintText && attrObj.hintText.trim() !== "";

      if (filteredMultiSelectValues.length !== multiSelectValues.length) {
        setPart(prev => ({
          ...prev,
          attributes: {
            ...prev.attributes,
            [key]: filteredMultiSelectValues
          }
        }));
      }

      if (!visibleAttrs.includes(num)) return null;
      let field = null;
      let unitsField = null;
      let hintText = null;

      if (fieldType === "text") {
        field = (
          <FormControl sx={{ width: '100%' }}>
            <TextField
              label={key}
              name={`attributes.${key}`}
              value={part.attributes?.[key] || ""}
              onChange={handleAttributeChange}
              fullWidth
              variant="outlined"
              error={!!(error && error[key])}
              helperText={error && error[key]}
              sx={{ '& .MuiInputBase-root': { height: '45px' } }}
            />
          </FormControl>
        );
      } else if (fieldType === "select") {
        if (enableMultiVals) {
          field = (
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>{key}</InputLabel>
              <Select
                label={key}
                name={`attributes.${key}`}
                multiple
                value={multiSelectValues}
                onChange={e => multiSelectChangeHandler(e, key)}
                renderValue={(selected) => (
                  <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val, idx) => (
                      <Chip key={idx} label={val} />
                    ))}
                  </MDBox>
                )}
                error={!!(error && error[key])}
                sx={{ height: '45px', '& .MuiSelect-select': { paddingY: '8px' } }}
                helperText={error && error[key]}
              >
                {options.split(";").map((option, i) => (
                  <MenuItem key={i} value={option}>
                    {option + " - " + attrObj.description.split(";")[i]}
                  </MenuItem>
                ))}
              </Select>
              {error && error[key] && (
                <MDTypography variant="caption" color="error">
                  {error[key]}
                </MDTypography>
              )}
            </FormControl>
          );
        } else {
          const CUSTOM_TOKEN = '\u2063{Custom Value Mode}\u2063';
          const optionsList = options.split(";");

          // Determine the current value for the selector
          const attrVal = part.attributes?.[key];
          let SelectorValue = "";
          if (attrVal == null || attrVal === "") {
            SelectorValue = "";
          } else if (optionsList.includes(attrVal)) {
            SelectorValue = attrVal;
          } else {
            SelectorValue = CUSTOM_TOKEN;
          }

          field = (
            <FormControl sx={{ width: '100%' }}>
              <TextField
                select
                label={key}
                name={`attributes.${key}`}
                value={SelectorValue}
                onChange={handleAttributeChange}
                fullWidth
                variant="outlined"
                error={!!(error && error[key])}
                helperText={error && error[key]}
                sx={{ '& .MuiInputBase-root': { height: '45px' } }}
              >
                {optionsList.map((option, i) => {
                  const isCustom = (option.toLowerCase() === "other") && (num !== "0");
                  return (
                    <MenuItem
                      key={i}
                      value={isCustom ? CUSTOM_TOKEN : option}
                      // Triggers dialog when "other" is already selected but user wants to change it.
                      onClick={() => {
                        if (isCustom && SelectorValue === CUSTOM_TOKEN) {
                          handleAttributeChange({ target: { name: `attributes.${key}`, value: CUSTOM_TOKEN } });
                        }
                      }}
                    >
                      {option + " - " + attrObj.description.split(";")[i]}
                    </MenuItem>
                  );
                })}
              </TextField>
            </FormControl>
          );
        }
      } else if (fieldType === "text-with-units") {
        field = (
          <FormControl sx={{ width: '100%' }}>
            <TextField
              label={key}
              name={`attributes.${key}`}
              value={part.attributes?.[key] || ""}
              onChange={handleAttributeChange}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: '45px' } }}
              variant="outlined"
              InputProps={{
                endAdornment: units[key]
                  ? <InputAdornment position="end">{units[key]}</InputAdornment>
                  : null,
              }}
              error={!!(error && error[key])}
              helperText={error && error[key]}
            />
          </FormControl>
        );
        unitsField = unitsList && (
          <FormControl sx={{ width: '100%' }}>
            <InputLabel>Unit</InputLabel>
            <Select
              name={key}
              value={units[key] || ""}
              onChange={unitsChangeHandler}
              fullWidth
              label="Unit"
              sx={{ height: '45px', '& .MuiSelect-select': { paddingY: '8px' } }}
            >
              {unitsList.split(";").map((unit, i) => (
                <MenuItem
                  key={i}
                  value={(unit === '') ? ' ' : unit}
                  sx={{ height: '30px', '& .MuiSelect-select': { paddingY: '8px' } }}
                >
                  {unit}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      hintText = hasHintText && (
        <MDBox component="span" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title={attrObj.hintText}>
            <HelpOutlineIcon fontSize='small' />
          </Tooltip>
        </MDBox>
      )

      const hasUnits = unitsList && fieldType === "text-with-units";
      const fieldSize = hasUnits ? (hasHintText ? 8.5 : 9) : (hasHintText ? 11.5 : 12);
      return (
        <Grid
          container
          spacing={1}
          key={num}
          alignItems="center"
          mt={1}
          className={`attribute-field attribute-field-${num}`}
          id={`attribute-field-${num}`}
        >
          <Grid item xs={fieldSize}>
            {field}
          </Grid>

          {hasUnits && (
            <Grid item xs={3}>
              {unitsField}
            </Grid>
          )}

          {hasHintText && (
            <Grid item xs={0.5}>
              {hintText}
            </Grid>
          )}

        </Grid>
      );
    });
  };

  return (
    <>
      <CustomTextDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        name={dialogNameValue}
        part={part}
      />
      <MDBox>
        {part.partCategory.has_attributes && (
          <MDTypography variant="h5" fontWeight="bold" mt={1}>
            Attributes :
          </MDTypography>)}
        {renderAttributeFields()}
      </MDBox>
    </>
  );
}